// Firebase initialization — Firestore, Auth (Google Workspace SSO), Functions.
//
// Config comes from Vite env vars (see .env.example). For an internal app the
// Firebase web config is not secret, but keeping it in env keeps prod/staging
// swappable and the repo clean.

import { initializeApp } from 'firebase/app'
import {
  getAuth,
  connectAuthEmulator,
  GoogleAuthProvider,
  signInWithPopup,
  signOut as fbSignOut,
  onAuthStateChanged,
} from 'firebase/auth'
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore'
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions'
import { GOOGLE_SCOPES } from './google.js'

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'dummy',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'dummy.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'dummy',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'dummy.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '0',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:0:web:0',
}

// The Google Workspace domain allowed to sign in (e.g. "careernomad.org").
// Used as the `hd` hint on the OAuth popup and re-checked after sign-in.
export const WORKSPACE_DOMAIN = import.meta.env.VITE_WORKSPACE_DOMAIN || ''

export const app = initializeApp(firebaseConfig)
export const auth = getAuth(app)
export const db = getFirestore(app)
export const functions = getFunctions(app)

// Point at local emulators during `npm run dev` against the emulator suite.
if (import.meta.env.VITE_USE_EMULATORS === 'true') {
  // Auth emulator: signInWithPopup then shows the emulator's own account picker
  // (no real Google needed). disableWarnings hides the dev-only banner.
  connectAuthEmulator(auth, 'http://127.0.0.1:9099', { disableWarnings: true })
  connectFirestoreEmulator(db, '127.0.0.1', 8080)
  connectFunctionsEmulator(functions, '127.0.0.1', 5001)
}

const provider = new GoogleAuthProvider()
if (WORKSPACE_DOMAIN) provider.setCustomParameters({ hd: WORKSPACE_DOMAIN })
GOOGLE_SCOPES.forEach((s) => provider.addScope(s))

export async function signInWithGoogle() {
  const result = await signInWithPopup(auth, provider)
  // Enforce the Workspace domain client-side; rules enforce it server-side.
  if (WORKSPACE_DOMAIN && result.user?.email) {
    const domain = result.user.email.split('@')[1]
    if (domain !== WORKSPACE_DOMAIN) {
      await fbSignOut(auth)
      throw new Error(`Sign-in restricted to @${WORKSPACE_DOMAIN} accounts.`)
    }
  }
  return result.user
}

export function signOut() {
  return fbSignOut(auth)
}

export function watchAuth(cb) {
  return onAuthStateChanged(auth, cb)
}
